import { useContext, useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import { QuestionOption, QuestionProps } from "./Props";

import type { BooleanExpr } from "@aidkitorg/types/lib/survey";
import InterfaceContext from "../Context";
import { useModularMarkdown } from "../Hooks/ModularMarkdown";
import { useLocalizedStrings } from "../Localization";
import { languageContent, safeParse } from "../Util";
import { fisherYatesShuffle, hash } from "../utils/orderRandomizer";
import { getOptionsFromProps, MarkdownLabel, shouldExcludeChoice } from "./SingleSelect";

type MultiSelectChoice = {
  value: string,
  excludeWhen?: BooleanExpr,
}

export function MultipleSelectQuestion(props: QuestionProps) {
  const L = useLocalizedStrings();
  const context = useContext(InterfaceContext);
  const metadata = safeParse(props["Metadata"] || '{}');
  const targetField = props["Target Field"]!;
  const fieldValue = props.info[props["Target Field"] || ''];

  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const hasSelectAll = props["Options (if relevant)"]?.some((o: any) => o['Select All'] === true);
  let options = getOptionsFromProps(props);

  const regex = /\$[a-zA-Z\\_][a-zA-Z\\_0-9]+/g;

  function replacer(match: string){
    const variable = match.slice(1).replace(/\\_/g,'_');

    // console.log("Found match:", variable, variable === "uid");

    if(variable === "uid") return props.uid;
    if(props.info[variable] !== undefined && props.info[variable] !== null) return props.info[variable];
    return match;
  }

  const marked = useModularMarkdown({
    content: props[languageContent(context.lang)] || '',
    info: props.info,
    replacer: str => str.replace(regex, replacer)
  });

  let seed: string | null = null;
  if (metadata?.component?.randomizeOrder && targetField) {
    seed = localStorage.getItem('ChoiceRandomizerSeed' + (props.uid || ''));
    if (seed === null) {
      seed = Math.random() + (props.uid || '');
      localStorage.setItem('ChoiceRandomizerSeed' + (props.uid || ''), seed);
    }
    options = fisherYatesShuffle(options, hash(0, seed), targetField);
  }

  const choiceToExcludeWhenMap: Record<string, BooleanExpr | undefined> = {};
  (metadata?.component?.choices as Array<MultiSelectChoice> | undefined)?.forEach((choice) => {
    choiceToExcludeWhenMap[choice.value] = choice.excludeWhen;
  });

  const isExcluded = (optionName: string) => {
    return shouldExcludeChoice(choiceToExcludeWhenMap[optionName], props.info);
  };

  const visibleOptions = options.filter((o: QuestionOption) => !isExcluded(o.Name));

  // Count of options that "Select All" should toggle: only visible, non-exclusive,
  // non-meta ("Select All") entries. Used both to drive the Select All click handler
  // and to determine whether the Select All checkbox should appear checked.
  const selectableVisibleCount = visibleOptions.reduce((total, o: QuestionOption) => {
    return o['Exclusive Option'] !== true && o['Select All'] !== true ? total + 1 : total;
  }, 0);

  // Multi-select stores selections as a CSV. If a previously-selected option
  // becomes excluded (because excludeWhen flips to true based on other answers),
  // strip it from the saved value so we don't silently retain hidden selections.
  useEffect(() => {
    if (!targetField) return;
    const current = props.info[targetField];
    if (!current) return;
    const parts = current.split(',').map((k: string) => k.trim()).filter((k: string) => k.length > 0);
    const filtered = parts.filter((k: string) => !isExcluded(k));
    if (filtered.length !== parts.length) {
      const next = filtered.join(',');
      props.setInfoKey(targetField, next, next.length > 0, false);
    }
    // We intentionally only re-run this when the underlying field value or props.info changes.
  }, [fieldValue, props.info]);

  useEffect(() => {
    // Determine if all (currently visible) options are selected and thus whether
    // the 'Select All' checkbox should appear checked. We must count against
    // `selectableVisibleCount` rather than the full options list, otherwise
    // any excluded option would make the count never match and Select All
    // would never appear checked even after the user has selected every
    // visible option.
    if (hasSelectAll) {
      const numOptionsSelected = fieldValue
        ? fieldValue.split(',').map((k: string) => k.trim()).filter((k: string) => k.length > 0).length
        : 0;

      if (selectableVisibleCount > 0 && selectableVisibleCount === numOptionsSelected) {
        if (!selectAllChecked) {
          setSelectAllChecked(true);
        }
      } else {
        if (selectAllChecked) {
          setSelectAllChecked(false);
        }
      }
    }
  }, [hasSelectAll, fieldValue, selectableVisibleCount]);

  function setChecked(option: QuestionOption, checked: boolean) {
    if (option['Exclusive Option'] === true) {
      if (!checked) {
        props.setInfoKey(props["Target Field"]!, '', false, false);
      } else {
        props.setInfoKey(props["Target Field"]!, option.Name, true, false);
      }
      return;
    }

    // 'Select All' option is more like a button for setting other options, not a true option
    if (option["Select All"] === true) {
      if (!checked) {
        props.setInfoKey(props["Target Field"] as string, "", false, false);
      } else {
        // If selecting all would exceed the max number of allowed selections, show an alert instead.
        // Count only currently-visible, selectable options — excluded options should not be selected
        // by Select All, so they shouldn't count against the cap either.
        if (selectableVisibleCount > metadata.max_allowed_selections) {
          alert(L.questions.choice.already_selected_max);
          return;
        }
        const checkedOptions: string[] = [];
        for (const opt of visibleOptions) {
          if (opt['Exclusive Option'] !== true && opt['Select All'] !== true) {
            checkedOptions.push(opt.Name);
          }
        }

        props.setInfoKey(props["Target Field"] as string, checkedOptions.join(","), true, false);
      }
      return;
    }

    const kind = option.Name;
    let old = (props.info[props["Target Field"]!] || "")
      .split(",")
      .filter((k: string) => {
        const trimmedKey = k.trim();
        if (trimmedKey.length === 0) return false;
        for (const o of options) {
          if (o.Name === trimmedKey && o["Exclusive Option"] === true) return false;
        }
        return true;
      });
    if (checked) {
      if (old.indexOf(kind) === -1) {
        if (old.length >= metadata.max_allowed_selections) {
          // Prevent selecting more than the max number of options
          alert(L.questions.choice.already_selected_max);
          return;
        }
        old.push(kind);
        const next = old.join(",");
        props.setInfoKey(props["Target Field"]!, next, next.length > 0, false);
      }
    } else {
      // Not checked, remove from info
      if (old.indexOf(kind) >= 0) {
        old = old.filter((k: string) => k !== kind);
        const next = old.join(",");
        props.setInfoKey(props["Target Field"]!, next, next.length > 0, false);

        if(kind === 'other' || option["Other Field"]) {
          props.setInfoKey(props["Target Field"]! + '_' + kind, "", true, false);
        }
      }
    }
  }

  function isChecked(o: QuestionOption): boolean {
    if (o['Select All']) {
      return selectAllChecked;
    }
    return (props.info[props["Target Field"]!] || "")
      .split(",")
      .map((k: string) => k.trim())
      .indexOf(o["Name"]) !== -1;
  }

  function updateOtherValue(o: QuestionOption, value: string) {
    props.setInfoKey(props["Target Field"] + '_' + o.Name, value, value.length > 0, false);
  }

  const name = props['Question'] + '_' + props["Target Field"]!;

  function generateCheckbox(o: QuestionOption): any {
    const fields: any[] = [];

    if (metadata.filter && props.info[metadata.filter] && !props.info[metadata.filter].includes(o['Name'])) {
      return fields;
    }

    fields.push(<Form.Check
      checked={isChecked(o)}
      onChange={(e: any) => setChecked(o, e.target.checked)}
      type="checkbox"
      name={name}
      id={name + o["Name"]}
      key={name + o["Name"]}
      label={ 
        <span className={`${context.textAlign === 'right' ? 'mr-4' : ''}`}>
          <MarkdownLabel text={(o as any)[languageContent(context.lang).replace('Content', 'Text')] || o.Name} />
        </span>
      } />
    );

    if((o["Name"] === "other" || o["Other Field"]) && isChecked(o)) {
      fields.push(
        <Form.Group key={name + "_" + o["Name"] + '_group'}>
          <Form.Control type="text"
            defaultValue={props.info[props['Target Field'] + '_' + o.Name]}
            onChange={(e: any) => updateOtherValue(o, e.target.value)}
            name={name + '_' + o.Name}
            key={name + '_' + o.Name}
          />
        </Form.Group>);
    }

    return fields;
  }

  return (
    <Form.Group>
      <fieldset>
        <legend>
          {marked}
        </legend>
        {visibleOptions?.map((o) => {
          return generateCheckbox(o)
        })}
      </fieldset>
    </Form.Group>
  );
}